import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Button,
  CircularProgress,
  Snackbar,
  Alert,
  Paper,
  List,
  ListItem,
  ListItemText,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Divider,
  TextField,
  InputAdornment,
  IconButton,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Grid,
  Chip,
  Pagination
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import FilterListIcon from '@mui/icons-material/FilterList';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import PersonIcon from '@mui/icons-material/Person';
import EmojiEventsIcon from '@mui/icons-material/EmojiEvents';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';

import Header from '../Header';
import Footer from '../Footer';
import MobileMenu from '../MobileMenu';
import FloatingMenu from '../FloatingMenu';
import apiClient from '../../api/apiClient';
import { getAccessToken } from '../../api/authUtils';

const GameResultsPage = () => {
  const { gameId } = useParams();
  const navigate = useNavigate();

  // Stato per i risultati
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [gameInfo, setGameInfo] = useState(null);

  // Stato per la paginazione
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);

  // Stato per i filtri
  const [searchQuery, setSearchQuery] = useState('');
  const [dateFilter, setDateFilter] = useState('all'); // 'all', 'today', 'week', 'month'
  const [showFilters, setShowFilters] = useState(false);

  // Stato per il risultato selezionato
  const [selectedResult, setSelectedResult] = useState(null);

  // Gestione menu e login
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [showFloatingMenu, setShowFloatingMenu] = useState(false);
  const [isLoggedIn, setIsLoggedIn] = useState(!!getAccessToken());

  // Snackbar
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [snackbarSeverity, setSnackbarSeverity] = useState('info');

  useEffect(() => {
    const handleScroll = () => {
      if (window.scrollY > 100) {
        setShowFloatingMenu(true);
      } else {
        setShowFloatingMenu(false);
      }
    };
    window.addEventListener('scroll', handleScroll);
    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, []);

  // Funzione per caricare i risultati del gioco
  const fetchGameResults = async () => {
    setLoading(true);
    setError(null);
    try {
      // Otteniamo prima le informazioni del gioco
      const gameResponse = await apiClient.get(`/api/advantage_games/games/${gameId}/`);
      setGameInfo(gameResponse.data);

      // Costruiamo i parametri di query per i filtri
      let queryParams = `?page=${page}`;
      
      if (searchQuery) {
        queryParams += `&student_name=${encodeURIComponent(searchQuery)}`;
      }
      
      if (dateFilter !== 'all') {
        const now = new Date();
        let fromDate;
        
        if (dateFilter === 'today') {
          fromDate = new Date(now.setHours(0, 0, 0, 0));
        } else if (dateFilter === 'week') {
          fromDate = new Date(now.setDate(now.getDate() - 7));
        } else if (dateFilter === 'month') {
          fromDate = new Date(now.setMonth(now.getMonth() - 1));
        }
        
        if (fromDate) {
          queryParams += `&from_date=${fromDate.toISOString().split('T')[0]}`;
        }
      }

      // Otteniamo i risultati del gioco con i filtri applicati
      const resultsResponse = await apiClient.get(`/api/advantage_games/games/${gameId}/results/${queryParams}`);
      
      setResults(resultsResponse.data.results || []);
      setTotalPages(Math.ceil((resultsResponse.data.count || 0) / 10)); // Assumendo 10 risultati per pagina
    } catch (err) {
      console.error('Errore durante il recupero dei risultati:', err);
      setError(err.response?.data?.detail || 'Errore durante il recupero dei risultati');
      setSnackbarMessage('Non sei autorizzato a visualizzare questi risultati o il gioco non esiste.');
      setSnackbarSeverity('error');
      setSnackbarOpen(true);
    } finally {
      setLoading(false);
    }
  };

  // Effetto per caricare i risultati quando cambia il gameId, la pagina o i filtri
  useEffect(() => {
    if (gameId) {
      fetchGameResults();
    }
  }, [gameId, page, searchQuery, dateFilter]);

  // Gestori di eventi
  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleSearchChange = (event) => {
    setSearchQuery(event.target.value);
    setPage(1); // Resetta la pagina quando cambia la ricerca
  };

  const handleDateFilterChange = (event) => {
    setDateFilter(event.target.value);
    setPage(1); // Resetta la pagina quando cambia il filtro
  };

  const handleToggleFilters = () => {
    setShowFilters(!showFilters);
  };

  const handleOpenDetails = (result) => {
    // Verifica se i dettagli delle attività sono presenti nel formato corretto
    console.log('Dettagli risultato:', result);
    setSelectedResult(result);
  };

  const handleCloseDetails = () => {
    setSelectedResult(null);
  };

  const handleCloseSnackbar = () => {
    setSnackbarOpen(false);
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleString('it-IT', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // Funzione per ottenere il colore del punteggio
  const getScoreColor = (score) => {
    if (score >= 90) return '#4CAF50'; // Verde
    if (score >= 70) return '#8BC34A'; // Verde chiaro
    if (score >= 60) return '#FFEB3B'; // Giallo
    if (score >= 50) return '#FFC107'; // Ambra
    return '#F44336'; // Rosso
  };

  return (
    <>
      <Header
        isLoggedIn={isLoggedIn}
        setIsLoggedIn={setIsLoggedIn}
        mobileMenuOpen={mobileMenuOpen}
        setMobileMenuOpen={setMobileMenuOpen}
        backgroundColor="#DF4634"
        textColor="#FFFFFF"
        logoImage="/logo/logobianco.svg"
      />
      {mobileMenuOpen && (
        <MobileMenu
          isLoggedIn={isLoggedIn}
          setIsLoggedIn={setIsLoggedIn}
          setMobileMenuOpen={setMobileMenuOpen}
        />
      )}
      {showFloatingMenu && (
        <FloatingMenu
          isLoggedIn={isLoggedIn}
          handleLogout={() => {
            localStorage.removeItem('access');
            localStorage.removeItem('refresh');
            setIsLoggedIn(false);
            navigate('/login');
          }}
        />
      )}

      <Box sx={{ minHeight: '100vh', backgroundColor: '#F3F6FB', padding: { xs: '1rem', md: '2rem' } }}>
        <Box sx={{ maxWidth: '1200px', margin: '0 auto' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', marginBottom: '1.5rem' }}>
            <IconButton 
              onClick={() => navigate(`/games/${gameId}`)} 
              sx={{ marginRight: '1rem', backgroundColor: 'rgba(0,0,0,0.05)' }}
            >
              <ArrowBackIcon />
            </IconButton>
            <Typography variant="h5" sx={{ fontWeight: 700 }}>
              {gameInfo ? gameInfo.title : 'Punteggi Salvati'}
            </Typography>
          </Box>

          <Paper sx={{ padding: '1.5rem', borderRadius: '1rem', marginBottom: '1.5rem' }}>
            <Grid container spacing={2} alignItems="center">
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  placeholder="Cerca per nome studente..."
                  value={searchQuery}
                  onChange={handleSearchChange}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon />
                      </InputAdornment>
                    ),
                  }}
                  variant="outlined"
                  size="small"
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <Box sx={{ display: 'flex', justifyContent: { xs: 'flex-start', md: 'flex-end' } }}>
                  <Button
                    startIcon={<FilterListIcon />}
                    onClick={handleToggleFilters}
                    sx={{ mr: 1 }}
                    color="primary"
                    variant="outlined"
                    size="small"
                  >
                    Filtri
                  </Button>
                  <Button
                    onClick={fetchGameResults}
                    color="primary"
                    variant="contained"
                    size="small"
                  >
                    Aggiorna
                  </Button>
                </Box>
              </Grid>
            </Grid>

            {showFilters && (
              <Box sx={{ marginTop: '1rem', padding: '1rem', backgroundColor: 'rgba(0, 0, 0, 0.02)', borderRadius: '0.5rem' }}>
                <Grid container spacing={2}>
                  <Grid item xs={12} md={6}>
                    <FormControl fullWidth variant="outlined" size="small">
                      <InputLabel id="date-filter-label">Filtra per data</InputLabel>
                      <Select
                        labelId="date-filter-label"
                        value={dateFilter}
                        onChange={handleDateFilterChange}
                        label="Filtra per data"
                        startAdornment={
                          <InputAdornment position="start">
                            <CalendarTodayIcon fontSize="small" />
                          </InputAdornment>
                        }
                      >
                        <MenuItem value="all">Tutti i periodi</MenuItem>
                        <MenuItem value="today">Oggi</MenuItem>
                        <MenuItem value="week">Ultima settimana</MenuItem>
                        <MenuItem value="month">Ultimo mese</MenuItem>
                      </Select>
                    </FormControl>
                  </Grid>
                </Grid>
              </Box>
            )}
          </Paper>

          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', padding: '3rem' }}>
              <CircularProgress sx={{ color: '#DF4634' }} />
            </Box>
          ) : error ? (
            <Paper sx={{ padding: '2rem', textAlign: 'center', borderRadius: '1rem' }}>
              <Typography variant="h6" color="error" gutterBottom>
                Si è verificato un errore
              </Typography>
              <Typography variant="body1">{error}</Typography>
              <Button 
                variant="contained" 
                sx={{ mt: 2 }} 
                onClick={() => navigate(`/games/${gameId}`)}
              >
                Torna al gioco
              </Button>
            </Paper>
          ) : results.length === 0 ? (
            <Paper sx={{ padding: '2rem', textAlign: 'center', borderRadius: '1rem' }}>
              <Typography variant="h6" gutterBottom>
                Nessun risultato trovato
              </Typography>
              <Typography variant="body1" sx={{ mb: 2, color: 'text.secondary' }}>
                Non ci sono punteggi salvati per questo gioco con i filtri selezionati.
              </Typography>
              <Button 
                variant="contained" 
                onClick={() => navigate(`/games/${gameId}`)}
              >
                Torna al gioco
              </Button>
            </Paper>
          ) : (
            <>
              <Paper sx={{ borderRadius: '1rem', overflow: 'hidden' }}>
                <List sx={{ padding: 0 }}>
                  {results.map((result) => (
                    <ListItem
                      key={result.submission_id}
                      divider
                      button
                      onClick={() => handleOpenDetails(result)}
                      sx={{
                        transition: 'all 0.2s',
                        '&:hover': {
                          backgroundColor: 'rgba(223, 70, 52, 0.05)',
                        },
                        padding: '1rem',
                      }}
                    >
                      <Grid container alignItems="center" spacing={2}>
                        <Grid item xs={12} sm={5}>
                          <Box sx={{ display: 'flex', alignItems: 'center' }}>
                            <PersonIcon sx={{ mr: 1, color: 'rgba(0, 0, 0, 0.54)' }} />
                            <ListItemText 
                              primary={result.student_name} 
                              secondary={`Completato: ${formatDate(result.completed_at)}`}
                            />
                          </Box>
                        </Grid>
                        <Grid item xs={12} sm={4} sx={{ display: 'flex', alignItems: 'center' }}>
                          <EmojiEventsIcon sx={{ mr: 1, color: getScoreColor(result.total_score) }} />
                          <Typography>
                            Punteggio: <strong>{result.total_score || 0}</strong>
                          </Typography>
                        </Grid>
                        <Grid item xs={12} sm={3} sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                          <Chip 
                            label="Visualizza dettagli" 
                            color="primary" 
                            size="small" 
                            variant="outlined"
                          />
                        </Grid>
                      </Grid>
                    </ListItem>
                  ))}
                </List>
              </Paper>

              {totalPages > 1 && (
                <Box sx={{ display: 'flex', justifyContent: 'center', marginTop: '2rem' }}>
                  <Pagination 
                    count={totalPages} 
                    page={page} 
                    onChange={handleChangePage} 
                    color="primary" 
                    size="large"
                  />
                </Box>
              )}
            </>
          )}

          <Box sx={{ marginTop: '2rem', textAlign: 'center' }}>
            <Button
              variant="contained"
              onClick={() => navigate(`/games/${gameId}`)}
              sx={{
                backgroundColor: '#DF4634',
                '&:hover': { backgroundColor: '#C73B2A' },
                borderRadius: '2rem',
                padding: '0.5rem 1.5rem',
              }}
            >
              Torna al gioco
            </Button>
          </Box>
        </Box>
      </Box>

      {/* Dialog per visualizzare i dettagli di un risultato */}
      {selectedResult && (
        <Dialog
          open={Boolean(selectedResult)}
          onClose={handleCloseDetails}
          fullWidth
          maxWidth="md"
          PaperProps={{
            sx: { borderRadius: '1rem' }
          }}
        >
          <DialogTitle sx={{ borderBottom: '1px solid rgba(0, 0, 0, 0.12)', padding: '1.5rem' }}>
            <Typography variant="h6">Dettagli Risultato</Typography>
          </DialogTitle>

          <DialogContent sx={{ padding: '1.5rem' }}>
            <Grid container spacing={3}>
              <Grid item xs={12} md={6}>
                <Box sx={{ mb: 2 }}>
                  <Typography variant="subtitle2" color="text.secondary">Studente</Typography>
                  <Typography variant="h6">{selectedResult.student_name}</Typography>
                </Box>
              </Grid>
              <Grid item xs={12} md={6}>
                <Box sx={{ mb: 2 }}>
                  <Typography variant="subtitle2" color="text.secondary">Punteggio Totale</Typography>
                  <Typography 
                    variant="h6" 
                    sx={{ 
                      fontWeight: 'bold', 
                      color: getScoreColor(selectedResult.total_score)
                    }}
                  >
                    {selectedResult.total_score || 0}/100
                  </Typography>
                </Box>
              </Grid>
              <Grid item xs={12} md={6}>
                <Box sx={{ mb: 2 }}>
                  <Typography variant="subtitle2" color="text.secondary">Data Completamento</Typography>
                  <Typography variant="body1">{formatDate(selectedResult.completed_at)}</Typography>
                </Box>
              </Grid>
              <Grid item xs={12} md={6}>
                <Box sx={{ mb: 2 }}>
                  <Typography variant="subtitle2" color="text.secondary">Tipo Attività</Typography>
                  <Typography variant="body1">{selectedResult.activity_type === 'verifica' ? 'Verifica' : 'Esercitazione'}</Typography>
                </Box>
              </Grid>
            </Grid>

            <Divider sx={{ my: 3 }} />

            <Typography variant="h6" sx={{ mb: 2 }}>Risultati Attività</Typography>

            {selectedResult.activity_results && selectedResult.activity_results.activities && selectedResult.activity_results.activities.length > 0 ? (
              selectedResult.activity_results.activities.map((activity, index) => (
                <Paper 
                  key={index} 
                  sx={{ 
                    mb: 3, 
                    p: 2, 
                    borderLeft: `4px solid ${getScoreColor(activity.score)}`,
                    borderRadius: '0.5rem'
                  }}
                >
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                    <Typography variant="subtitle1" sx={{ fontWeight: 'bold' }}>
                      {activity.title || `Attività ${index + 1}`}
                    </Typography>
                    <Chip 
                      label={`${activity.score || 0}/100`} 
                      size="small" 
                      sx={{ 
                        backgroundColor: getScoreColor(activity.score),
                        color: 'white',
                        fontWeight: 'bold'
                      }} 
                    />
                  </Box>
                  
                  <Grid container spacing={2} sx={{ mb: 2 }}>
                    <Grid item xs={12} sm={4}>
                      <Typography variant="body2" color="text.secondary">
                        Tipo: {activity.activity_type || 'Non specificato'}
                      </Typography>
                    </Grid>
                    
                    {activity.correctAnswers !== undefined && activity.totalQuestions !== undefined && (
                      <Grid item xs={12} sm={8}>
                        <Typography variant="body2" sx={{ display: 'flex', alignItems: 'center' }}>
                          Risposte corrette: <b style={{ marginLeft: '4px' }}>{activity.correctAnswers}/{activity.totalQuestions}</b>
                          <span style={{ 
                            display: 'inline-block', 
                            width: '120px', 
                            height: '8px', 
                            backgroundColor: '#f0f0f0', 
                            marginLeft: '8px',
                            borderRadius: '4px',
                            overflow: 'hidden'
                          }}>
                            <span style={{
                              display: 'block',
                              width: `${(activity.correctAnswers / activity.totalQuestions) * 100}%`,
                              height: '100%',
                              backgroundColor: getScoreColor(activity.score),
                            }}/>
                          </span>
                        </Typography>
                      </Grid>
                    )}
                  </Grid>

                  {/* Visualizzazione delle singole domande e risposte */}
                  {activity.type === 'quiz' && activity.questionResults && activity.questionResults.length > 0 && (
                    <Box sx={{ mt: 2 }}>
                      <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: 1 }}>
                        Dettaglio Domande Quiz
                      </Typography>
                      
                      {activity.questionResults.map((question, qIndex) => (
                        <Paper
                          key={qIndex}
                          sx={{
                            p: 1.5,
                            mb: 1.5,
                            backgroundColor: question.isCorrect === true 
                              ? 'rgba(76, 175, 80, 0.1)' 
                              : question.isCorrect === false 
                                ? 'rgba(244, 67, 54, 0.1)' 
                                : 'rgba(0, 0, 0, 0.05)',
                            borderRadius: '4px'
                          }}
                        >
                          <Typography variant="body1" sx={{ fontWeight: 'medium', mb: 1 }}>
                            {qIndex + 1}. {question.question}
                          </Typography>
                          
                          {question.type === 'multiple_choice' ? (
                            <>
                              <Box sx={{ display: 'flex', alignItems: 'center', mb: 0.5 }}>
                                <Typography variant="body2" sx={{ minWidth: '120px', color: 'text.secondary' }}>
                                  Risposta data:
                                </Typography>
                                <Typography 
                                  variant="body2" 
                                  sx={{ 
                                    fontWeight: 'bold',
                                    color: question.isCorrect ? 'success.main' : 'error.main'
                                  }}
                                >
                                  {question.userAnswerText || question.userAnswer || 'Nessuna risposta'}
                                </Typography>
                              </Box>
                              
                              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                <Typography variant="body2" sx={{ minWidth: '120px', color: 'text.secondary' }}>
                                  Risposta corretta:
                                </Typography>
                                <Typography variant="body2" sx={{ fontWeight: 'bold', color: 'success.main' }}>
                                  {question.correctAnswerText || question.correctAnswer}
                                </Typography>
                              </Box>
                            </>
                          ) : (
                            <Box sx={{ mb: 0.5 }}>
                              <Typography variant="body2" sx={{ color: 'text.secondary', mb: 0.5 }}>
                                Risposta (aperta):
                              </Typography>
                              <Typography variant="body2" sx={{ fontStyle: 'italic', p: 1, backgroundColor: 'rgba(0,0,0,0.03)', borderRadius: '4px' }}>
                                {question.userAnswerText || question.userAnswer || 'Nessuna risposta'}
                              </Typography>
                            </Box>
                          )}
                          
                          {question.isCorrect !== null && (
                            <Box sx={{ mt: 1, display: 'flex', justifyContent: 'flex-end' }}>
                              <Chip 
                                size="small" 
                                label={question.isCorrect ? "Corretto" : "Errato"} 
                                color={question.isCorrect ? "success" : "error"}
                                variant="outlined"
                              />
                            </Box>
                          )}
                        </Paper>
                      ))}
                    </Box>
                  )}
                  
                  {/* Visualizzazione Fill the Box */}
                  {activity.type === 'fillthebox' && activity.results && activity.results.length > 0 && (
                    <Box sx={{ mt: 2 }}>
                      <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: 1 }}>
                        Dettaglio Riempimento Box
                      </Typography>
                      
                      {activity.results.map((result, rIndex) => (
                        <Paper
                          key={rIndex}
                          sx={{
                            p: 1.5,
                            mb: 1.5,
                            backgroundColor: result.isCorrect ? 'rgba(76, 175, 80, 0.1)' : 'rgba(244, 67, 54, 0.1)',
                            borderRadius: '4px'
                          }}
                        >
                          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <Typography variant="body1" sx={{ fontWeight: 'medium' }}>
                              Box {result.id}
                            </Typography>
                            <Chip 
                              size="small" 
                              label={result.isCorrect ? "Corretto" : "Errato"} 
                              color={result.isCorrect ? "success" : "error"}
                              variant="outlined"
                            />
                          </Box>
                          
                          <Box sx={{ display: 'flex', alignItems: 'center', mb: 0.5, mt: 1 }}>
                            <Typography variant="body2" sx={{ minWidth: '120px', color: 'text.secondary' }}>
                              Risposta data:
                            </Typography>
                            <Typography 
                              variant="body2" 
                              sx={{ 
                                fontWeight: 'bold',
                                color: result.isCorrect ? 'success.main' : 'error.main'
                              }}
                            >
                              {result.userAnswerText || result.userAnswer || 'Nessuna risposta'}
                            </Typography>
                          </Box>
                          
                          <Box sx={{ display: 'flex', alignItems: 'center' }}>
                            <Typography variant="body2" sx={{ minWidth: '120px', color: 'text.secondary' }}>
                              Risposta corretta:
                            </Typography>
                            <Typography variant="body2" sx={{ fontWeight: 'bold', color: 'success.main' }}>
                              {result.correctAnswerText || result.correctAnswer}
                            </Typography>
                          </Box>
                        </Paper>
                      ))}
                    </Box>
                  )}
                  
                  {/* Visualizzazione Select Group */}
                  {activity.type === 'selectgroup' && activity.groupResults && activity.groupResults.length > 0 && (
                    <Box sx={{ mt: 2 }}>
                      <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: 1 }}>
                        Dettaglio Gruppi
                      </Typography>
                      
                      {activity.groupResults.map((group, gIndex) => (
                        <Paper
                          key={gIndex}
                          sx={{
                            p: 1.5,
                            mb: 1.5,
                            backgroundColor: group.isCorrect ? 'rgba(76, 175, 80, 0.1)' : 'rgba(244, 67, 54, 0.1)',
                            borderRadius: '4px'
                          }}
                        >
                          <Typography variant="body1" sx={{ fontWeight: 'medium', mb: 1 }}>
                            Gruppo: {group.groupName}
                          </Typography>
                          
                          <Box sx={{ mb: 1 }}>
                            <Typography variant="body2" sx={{ color: 'text.secondary', mb: 0.5 }}>
                              Elementi selezionati:
                            </Typography>
                            {group.userSelection && group.userSelection.length > 0 ? (
                              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                                {group.userSelection.map((item, iIndex) => (
                                  <Chip 
                                    key={iIndex}
                                    label={item} 
                                    size="small" 
                                    color={group.correctSelection.includes(item) ? "success" : "error"}
                                  />
                                ))}
                              </Box>
                            ) : (
                              <Typography variant="body2" sx={{ fontStyle: 'italic' }}>
                                Nessun elemento selezionato
                              </Typography>
                            )}
                          </Box>
                          
                          <Box>
                            <Typography variant="body2" sx={{ color: 'text.secondary', mb: 0.5 }}>
                              Elementi corretti:
                            </Typography>
                            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                              {group.correctSelection.map((item, iIndex) => (
                                <Chip 
                                  key={iIndex}
                                  label={item} 
                                  size="small" 
                                  color="success"
                                  variant={group.userSelection.includes(item) ? "filled" : "outlined"}
                                />
                              ))}
                            </Box>
                          </Box>
                        </Paper>
                      ))}
                    </Box>
                  )}
                  
                  {/* Visualizzazione Crossword */}
                  {activity.type === 'crossword' && activity.wordResults && activity.wordResults.length > 0 && (
                    <Box sx={{ mt: 2 }}>
                      <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: 1 }}>
                        Dettaglio Cruciverba
                      </Typography>
                      
                      {activity.wordResults.map((word, wIndex) => (
                        <Paper
                          key={wIndex}
                          sx={{
                            p: 1.5,
                            mb: 1.5,
                            backgroundColor: word.isCorrect ? 'rgba(76, 175, 80, 0.1)' : 'rgba(244, 67, 54, 0.1)',
                            borderRadius: '4px'
                          }}
                        >
                          <Typography variant="body1" sx={{ fontWeight: 'medium', mb: 1 }}>
                            {word.wordNumber} {word.direction}: {word.clue}
                          </Typography>
                          
                          <Box sx={{ display: 'flex', alignItems: 'center', mb: 0.5 }}>
                            <Typography variant="body2" sx={{ minWidth: '120px', color: 'text.secondary' }}>
                              Risposta inserita:
                            </Typography>
                            <Typography 
                              variant="body2" 
                              sx={{ 
                                fontWeight: 'bold',
                                color: word.isCorrect ? 'success.main' : 'error.main'
                              }}
                            >
                              {word.userAnswer.replace(/_/g, '■')}
                            </Typography>
                          </Box>
                          
                          <Box sx={{ display: 'flex', alignItems: 'center' }}>
                            <Typography variant="body2" sx={{ minWidth: '120px', color: 'text.secondary' }}>
                              Parola corretta:
                            </Typography>
                            <Typography variant="body2" sx={{ fontWeight: 'bold', color: 'success.main' }}>
                              {word.correctAnswer}
                            </Typography>
                          </Box>
                        </Paper>
                      ))}
                    </Box>
                  )}
                  
                  {/* Visualizzazione Rearrange (Riordina) */}
                  {activity.type === 'rearrange' && activity.arrangementResults && activity.arrangementResults.length > 0 && (
                    <Box sx={{ mt: 2 }}>
                      <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: 1 }}>
                        Dettaglio Riordino Frasi
                      </Typography>
                      
                      <Paper
                        sx={{
                          p: 1.5,
                          mb: 2,
                          backgroundColor: 'rgba(0, 0, 0, 0.02)',
                          borderRadius: '4px'
                        }}
                      >
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                          <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                            Riordini corretti:
                          </Typography>
                          <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                            {activity.correctArrangements || activity.details?.correctArrangements || 0} su {activity.totalExercises || activity.details?.totalExercises || activity.arrangementResults.length}
                          </Typography>
                        </Box>
                        
                        <Box sx={{ width: '100%', height: '8px', backgroundColor: '#f0f0f0', borderRadius: '4px', overflow: 'hidden' }}>
                          <Box 
                            sx={{ 
                              height: '100%', 
                              width: `${activity.percentage || activity.details?.percentage || 0}%`,
                              backgroundColor: getScoreColor(activity.score)
                            }}
                          />
                        </Box>
                      </Paper>
                      
                      {activity.arrangementResults.map((arrangement, aIndex) => (
                        <Paper
                          key={aIndex}
                          sx={{
                            p: 1.5,
                            mb: 1.5,
                            backgroundColor: arrangement.isCorrect ? 'rgba(76, 175, 80, 0.1)' : 'rgba(244, 67, 54, 0.1)',
                            borderRadius: '4px'
                          }}
                        >
                          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                            <Typography variant="body1" sx={{ fontWeight: 'medium' }}>
                              {arrangement.topic || `Esercizio ${aIndex + 1}`}
                            </Typography>
                            <Chip 
                              size="small" 
                              label={arrangement.isCorrect ? "Corretto" : "Errato"} 
                              color={arrangement.isCorrect ? "success" : "error"}
                              variant="outlined"
                            />
                          </Box>
                          
                          <Box sx={{ mb: 2 }}>
                            <Typography variant="body2" sx={{ color: 'text.secondary', mb: 0.5 }}>
                              Riordino dell'utente:
                            </Typography>
                            <Box sx={{ 
                              display: 'flex', 
                              flexWrap: 'wrap', 
                              gap: '8px',
                              p: 1.5,
                              backgroundColor: 'rgba(0,0,0,0.03)',
                              borderRadius: '4px'
                            }}>
                              {arrangement.userArrangement && arrangement.userArrangement.map((word, wIndex) => (
                                <Chip 
                                  key={wIndex}
                                  label={word} 
                                  size="small" 
                                  sx={{ 
                                    backgroundColor: arrangement.isCorrect ? 'rgba(76, 175, 80, 0.1)' : 'rgba(244, 67, 54, 0.05)',
                                    border: '1px solid',
                                    borderColor: arrangement.isCorrect ? 'rgba(76, 175, 80, 0.5)' : 'rgba(244, 67, 54, 0.2)'
                                  }}
                                />
                              ))}
                            </Box>
                          </Box>
                          
                          <Box>
                            <Typography variant="body2" sx={{ color: 'text.secondary', mb: 0.5 }}>
                              Riordino corretto:
                            </Typography>
                            <Box sx={{ 
                              display: 'flex', 
                              flexWrap: 'wrap', 
                              gap: '8px',
                              p: 1.5,
                              backgroundColor: 'rgba(76, 175, 80, 0.05)',
                              borderRadius: '4px',
                              border: '1px solid rgba(76, 175, 80, 0.2)'
                            }}>
                              {arrangement.correctArrangement && arrangement.correctArrangement.map((word, wIndex) => (
                                <Chip 
                                  key={wIndex}
                                  label={word} 
                                  size="small" 
                                  sx={{ 
                                    backgroundColor: 'rgba(76, 175, 80, 0.1)',
                                    border: '1px solid rgba(76, 175, 80, 0.3)'
                                  }}
                                />
                              ))}
                            </Box>
                          </Box>
                        </Paper>
                      ))}
                    </Box>
                  )}
                  
                  {/* Visualizzazione Match Pairs */}
                  {activity.type === 'matchpairs' && activity.details && (
                    <Box sx={{ mt: 2 }}>
                      <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: 1 }}>
                        Dettaglio Associazioni
                      </Typography>
                      
                      <Paper
                        sx={{
                          p: 1.5,
                          backgroundColor: 'rgba(0, 0, 0, 0.02)',
                          borderRadius: '4px',
                          mb: 2
                        }}
                      >
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                          <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                            Coppie corrette:
                          </Typography>
                          <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                            {activity.details.totalCorrect} su {activity.details.totalPossible}
                          </Typography>
                        </Box>
                        
                        <Box sx={{ width: '100%', height: '8px', backgroundColor: '#f0f0f0', borderRadius: '4px', overflow: 'hidden' }}>
                          <Box 
                            sx={{ 
                              height: '100%', 
                              width: `${(activity.details.totalCorrect / activity.details.totalPossible) * 100}%`,
                              backgroundColor: getScoreColor(activity.score)
                            }}
                          />
                        </Box>
                      </Paper>
                      
                      {activity.pairResults && activity.pairResults.length > 0 ? (
                        activity.pairResults.map((pair, pIndex) => (
                          <Paper
                            key={pIndex}
                            sx={{
                              p: 1.5,
                              mb: 1.5,
                              backgroundColor: pair.isCorrect ? 'rgba(76, 175, 80, 0.1)' : 'rgba(244, 67, 54, 0.1)',
                              borderRadius: '4px'
                            }}
                          >
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                              <Typography variant="body1" sx={{ fontWeight: 'medium' }}>
                                Coppia {pIndex + 1}
                              </Typography>
                              <Chip 
                                size="small" 
                                label={pair.isCorrect ? "Corretto" : "Errato"} 
                                color={pair.isCorrect ? "success" : "error"}
                                variant="outlined"
                              />
                            </Box>
                            
                            <Grid container spacing={1}>
                              <Grid item xs={6}>
                                <Typography variant="body2" sx={{ color: 'text.secondary', mb: 0.5 }}>
                                  Elemento sinistro:
                                </Typography>
                                <Typography variant="body2" sx={{ fontWeight: 'medium' }}>
                                  {pair.leftItem || "—"}
                                </Typography>
                              </Grid>
                              
                              <Grid item xs={6}>
                                <Typography variant="body2" sx={{ color: 'text.secondary', mb: 0.5 }}>
                                  Elemento destro:
                                </Typography>
                                <Typography variant="body2" sx={{ fontWeight: 'medium' }}>
                                  {pair.rightItem || "—"}
                                </Typography>
                              </Grid>
                            </Grid>
                            
                            <Box sx={{ mt: 1.5 }}>
                              <Typography variant="body2" sx={{ color: 'text.secondary', mb: 0.5 }}>
                                Associazione dell'utente:
                              </Typography>
                              <Box sx={{ p: 1, backgroundColor: 'rgba(0,0,0,0.03)', borderRadius: '4px', display: 'flex', justifyContent: 'center' }}>
                                <Typography 
                                  variant="body2" 
                                  sx={{ 
                                    fontWeight: 'bold',
                                    color: pair.isCorrect ? 'success.main' : 'error.main'
                                  }}
                                >
                                  {pair.userMatch || "Nessuna associazione"}
                                </Typography>
                              </Box>
                            </Box>
                            
                            {!pair.isCorrect && (
                              <Box sx={{ mt: 1.5 }}>
                                <Typography variant="body2" sx={{ color: 'text.secondary', mb: 0.5 }}>
                                  Associazione corretta:
                                </Typography>
                                <Box sx={{ p: 1, backgroundColor: 'rgba(76, 175, 80, 0.1)', borderRadius: '4px', display: 'flex', justifyContent: 'center' }}>
                                  <Typography variant="body2" sx={{ fontWeight: 'bold', color: 'success.main' }}>
                                    {pair.correctMatch || "—"}
                                  </Typography>
                                </Box>
                              </Box>
                            )}
                          </Paper>
                        ))
                      ) : (
                        <Box sx={{ textAlign: 'center', py: 2, px: 1.5, backgroundColor: 'rgba(0,0,0,0.02)', borderRadius: '4px' }}>
                          <Typography variant="body2" color="text.secondary">
                            Informazioni dettagliate sulle associazioni non disponibili, ma l'utente ha completato {activity.details.totalCorrect} su {activity.details.totalPossible} coppie correttamente.
                          </Typography>
                          
                          {activity.details.percentage !== undefined && (
                            <Box sx={{ mt: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1 }}>
                              <Typography variant="body2" sx={{ fontWeight: 'medium' }}>
                                Percentuale di successo:
                              </Typography>
                              <Chip 
                                label={`${activity.details.percentage}%`} 
                                size="small" 
                                sx={{ 
                                  backgroundColor: getScoreColor(activity.details.percentage),
                                  color: 'white',
                                  fontWeight: 'bold'
                                }} 
                              />
                            </Box>
                          )}
                        </Box>
                      )}
                    </Box>
                  )}
                  
                  {/* Visualizzazione attività di Scrittura (testo o video) */}
                  {activity.type === 'scrittura' && activity.details && (
                    <Box sx={{ mt: 2 }}>
                      <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: 1 }}>
                        Dettaglio Contenuto
                      </Typography>
                      
                      <Paper
                        sx={{
                          p: 1.5,
                          backgroundColor: 'rgba(0, 0, 0, 0.02)',
                          borderRadius: '4px'
                        }}
                      >
                        <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                          <Typography variant="body2" sx={{ color: 'text.secondary', mr: 1 }}>
                            Tipo di contenuto:
                          </Typography>
                          <Chip 
                            label={activity.contentType === 'text' ? 'Testo' : activity.contentType === 'youtube' ? 'Video YouTube' : activity.contentType} 
                            size="small" 
                            color="primary"
                          />
                        </Box>
                        
                        <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                          {activity.details.message || 'Contenuto visualizzato dallo studente'}
                        </Typography>
                      </Paper>
                    </Box>
                  )}
                  
                  {/* Mostra statistiche aggiuntive se disponibili */}
                  {activity.details && (
                    <Box sx={{ mt: 2, p: 1.5, backgroundColor: 'rgba(0, 0, 0, 0.02)', borderRadius: '4px' }}>
                      <Typography variant="subtitle2" sx={{ mb: 1 }}>
                        Statistiche Quiz
                      </Typography>
                      
                      <Grid container spacing={1}>
                        {activity.details.multipleChoiceQuestions !== undefined && (
                          <Grid item xs={6} sm={4}>
                            <Typography variant="body2" color="text.secondary">
                              Domande a scelta multipla: <b>{activity.details.multipleChoiceQuestions}</b>
                            </Typography>
                          </Grid>
                        )}
                        
                        {activity.details.openEndedQuestions !== undefined && (
                          <Grid item xs={6} sm={4}>
                            <Typography variant="body2" color="text.secondary">
                              Domande aperte: <b>{activity.details.openEndedQuestions}</b>
                            </Typography>
                          </Grid>
                        )}
                        
                        {activity.details.totalCorrect !== undefined && (
                          <Grid item xs={6} sm={4}>
                            <Typography variant="body2" color="text.secondary">
                              Totale risposte corrette: <b>{activity.details.totalCorrect}</b>
                            </Typography>
                          </Grid>
                        )}
                        
                        {activity.details.percentage !== undefined && (
                          <Grid item xs={6} sm={4}>
                            <Typography variant="body2" color="text.secondary">
                              Percentuale successo: <b>{activity.details.percentage}%</b>
                            </Typography>
                          </Grid>
                        )}
                      </Grid>
                    </Box>
                  )}
                </Paper>
              ))
            ) : (
              <Typography variant="body1" color="text.secondary" sx={{ textAlign: 'center', py: 3 }}>
                Nessun dettaglio attività disponibile
              </Typography>
            )}
          </DialogContent>

          <DialogActions sx={{ padding: '1rem 1.5rem' }}>
            <Button onClick={handleCloseDetails} color="primary" variant="outlined">
              Chiudi
            </Button>
          </DialogActions>
        </Dialog>
      )}

      <Snackbar
        open={snackbarOpen}
        autoHideDuration={5000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={handleCloseSnackbar}
          severity={snackbarSeverity}
          sx={{ width: '100%' }}
        >
          {snackbarMessage}
        </Alert>
      </Snackbar>

      <Footer />
    </>
  );
};

export default GameResultsPage; 